<template>
  <AIVDialog
    v-model="isVisible"
    title="Set Location on Map"
    width="60%"
    class="mobile-friendly-modal map-pin-picker-modal"
  >
    <div class="map-pin-picker-content">
      <p class="instructions">
        Click on the map to place a pin at the correct location for this address.
      </p>
      <div ref="mapContainer" class="map-container"></div>
      <div v-if="selectedLat !== null" class="selected-coords">
        <i class="fa-solid fa-map-pin"></i>
        <span>{{ selectedLat.toFixed(6) }}, {{ selectedLng.toFixed(6) }}</span>
        <span v-if="resolvedAddress" class="resolved-address">
          &mdash; {{ resolvedAddress }}
        </span>
      </div>
      <div v-else class="selected-coords no-selection">
        <i class="fa-solid fa-hand-pointer"></i>
        Click the map to place a pin
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button
          type="primary"
          :disabled="selectedLat === null"
          :loading="resolving"
          @click="handleConfirm"
        >
          <i class="fa-solid fa-check"></i> Confirm Location
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import CommonAPIService from "@/serviceHandlers/commonHandler";
import router from "src/routes/routes";

const api = new CommonAPIService(router);

let googleMapsPromise = null;
let cachedApiKey = null;

function loadGoogleMapsApi(apiKey) {
  if (googleMapsPromise) return googleMapsPromise;
  if (window.google && window.google.maps) {
    return Promise.resolve();
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => {
      googleMapsPromise = null;
      reject(new Error("Failed to load Google Maps API"));
    };
    document.head.appendChild(script);
  });
  return googleMapsPromise;
}

export default {
  name: "AIVMapPinPicker",
  components: { AIVDialog },

  props: {
    visible: { type: Boolean, default: false },
    // Optional initial center: existing geocode string "lat, lng"
    geocode: { type: String, default: "" },
    // Optional address fields for initial center if no geocode
    address: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      map: null,
      marker: null,
      selectedLat: null,
      selectedLng: null,
      resolvedAddress: "",
      resolving: false,
    };
  },

  computed: {
    isVisible: {
      get() { return this.visible; },
      set(val) { this.$emit("update:visible", val); },
    },
  },

  watch: {
    visible(val) {
      if (val) {
        // Wait for dialog transition to complete so the map container is in the DOM
        this.$nextTick(() => {
          setTimeout(() => this.initMap(), 300);
        });
      }
    },
  },

  methods: {
    async initMap() {
      try {
        if (!this.$refs.mapContainer) return;

        if (!cachedApiKey) {
          const response = await api.getCall("/api/geocoding/api-key");
          cachedApiKey = response.apiKey;
        }
        await loadGoogleMapsApi(cachedApiKey);

        // Double-check container is still in DOM after async loading
        if (!this.$refs.mapContainer) return;

        // Determine initial center
        let center = { lat: 39.32, lng: -111.09 }; // Utah default
        let zoom = 7;

        if (this.geocode && this.geocode.includes(",")) {
          const [lat, lng] = this.geocode.split(",").map((s) => parseFloat(s.trim()));
          if (!isNaN(lat) && !isNaN(lng)) {
            center = { lat, lng };
            zoom = 16;
          }
        } else if (this.address && (this.address.city || this.address.state)) {
          // Try to geocode the partial address for initial centering
          try {
            const parts = [this.address.city, this.address.state, this.address.zip]
              .filter(Boolean).join(", ");
            if (parts) {
              const result = await api.getCall("/api/geocoding", { address: parts });
              if (result && result.lat && result.lng) {
                center = { lat: result.lat, lng: result.lng };
                zoom = 13;
              }
            }
          } catch (e) {
            // Ignore — use default center
          }
        }

        this.selectedLat = null;
        this.selectedLng = null;
        this.resolvedAddress = "";

        this.map = new google.maps.Map(this.$refs.mapContainer, {
          center,
          zoom,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
        });

        // If we have an existing geocode, place the marker there
        if (zoom >= 16) {
          this.placeMarker(center);
        }

        this.map.addListener("click", (e) => {
          this.placeMarker({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        });
      } catch (err) {
        this.$notifyError("Failed to load map: " + err.message);
      }
    },

    placeMarker(position) {
      if (this.marker) {
        this.marker.setPosition(position);
      } else {
        this.marker = new google.maps.Marker({
          position,
          map: this.map,
          draggable: true,
        });
        this.marker.addListener("dragend", (e) => {
          this.selectedLat = e.latLng.lat();
          this.selectedLng = e.latLng.lng();
          this.reverseGeocodeSelection();
        });
      }
      this.selectedLat = position.lat;
      this.selectedLng = position.lng;
      this.reverseGeocodeSelection();
    },

    async reverseGeocodeSelection() {
      this.resolving = true;
      this.resolvedAddress = "";
      try {
        const result = await api.getCall("/api/geocoding/reverse", {
          lat: this.selectedLat,
          lng: this.selectedLng,
        });
        this.resolvedAddress = result.formatted_address || "";
      } catch (e) {
        // Not critical — just won't show resolved address
      } finally {
        this.resolving = false;
      }
    },

    handleConfirm() {
      if (this.selectedLat === null) return;
      this.$emit("location-selected", {
        lat: this.selectedLat,
        lng: this.selectedLng,
        geocode: `${this.selectedLat}, ${this.selectedLng}`,
        resolvedAddress: this.resolvedAddress,
      });
      this.isVisible = false;
    },

    handleCancel() {
      this.isVisible = false;
    },
  },
};
</script>

<style scoped>
.map-pin-picker-content {
  padding: 0 1rem;
}

.instructions {
  color: #ccc;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.map-container {
  width: 100%;
  height: 400px;
  border-radius: 8px;
  border: 1px solid #555;
}

.selected-coords {
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: rgba(107, 166, 205, 0.15);
  border: 1px solid rgba(107, 166, 205, 0.3);
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 0.9rem;
}

.selected-coords i {
  color: #6ba6cd;
  margin-right: 0.5rem;
}

.selected-coords.no-selection {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #999;
}

.resolved-address {
  color: #aaa;
  font-size: 0.85rem;
}

.text-center {
  text-align: center;
}

@media (max-width: 768px) {
  :deep(.el-dialog) {
    width: 95% !important;
    margin: 5px auto !important;
  }

  .map-container {
    height: 300px;
  }
}
</style>
